import { useEffect, useState } from 'react';
import { MegaphoneIcon } from '@heroicons/react/24/outline';
import {
  Alert,
  Button,
  Input,
  Modal,
  ModalFooter,
  MultiSelect,
  Select,
  Switch,
  Textarea,
} from '@components/ui';
import { BannerContenido } from './BannerContenido';
import { mensajeDeError, useBannerMutaciones } from '../hooks/useBannerAdmin';
import {
  LAYOUTS_BANNER,
  PORTALES_BANNER,
  ROLES_BANNER,
  TIPOS_BANNER,
  type BannerAdmin,
  type BannerFormulario,
  type BannerLayout,
} from '../types/announcement.types';

/**
 * Alta y edición de un banner.
 *
 * Las fechas se manejan como `YYYY-MM-DD` en los inputs y se convierten a ISO
 * al guardar. La conversión es explícita en vez de `new Date(str)` porque ese
 * constructor interpreta `YYYY-MM-DD` en UTC: en Panamá (UTC-5) una campaña que
 * termina el 30 se apagaría el 29 a las 19:00.
 */

/**
 * `YYYY-MM-DD` a partir de los componentes LOCALES de la fecha.
 *
 * No se usa `toISOString().slice(0,10)`, que da la fecha en UTC: en Panamá
 * (UTC-5) las 23:59 del 31 de diciembre son ya el 1 de enero en UTC, así que el
 * campo "Hasta" mostraba 01/01/2100 para un banner que termina el 31/12/2099. Y
 * pasadas las 19:00, `HOY()` devolvía mañana.
 */
function aInputDate(fecha: Date): string {
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');
  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

const HOY = () => aInputDate(new Date());

function isoAInputDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? HOY() : aInputDate(d);
}

/** `YYYY-MM-DD` → ISO. `finDelDia` empuja al último instante para que el día de fin cuente entero. */
function aIso(fecha: string, finDelDia = false): string {
  const [a, m, d] = fecha.split('-').map(Number);
  if (!a || !m || !d) return new Date().toISOString();
  return finDelDia
    ? new Date(a, m - 1, d, 23, 59, 59).toISOString()
    : new Date(a, m - 1, d, 0, 0, 0).toISOString();
}

const VACIO: BannerFormulario = {
  isActive: true,
  priority: 0,
  startDate: aIso(HOY()),
  endDate: aIso('2099-12-31', true),
  target: { tenants: [], companies: [], roles: [], portals: [] },
  bannerAlertType: 'info',
  content: '',
  layout: 'alerta',
};

export function BannerFormModal({
  abierto,
  banner,
  onCerrar,
}: {
  abierto: boolean;
  /** `null` = crear. */
  banner: BannerAdmin | null;
  onCerrar: () => void;
}) {
  const { crear, actualizar } = useBannerMutaciones();
  const [form, setForm] = useState<BannerFormulario>(VACIO);
  const [error, setError] = useState<string | null>(null);

  // Recargar al abrir, no al montar: el modal se monta una vez y se reutiliza
  // para cada banner, así que sin esto el segundo que abras muestra el primero.
  useEffect(() => {
    if (!abierto) return;
    setError(null);
    setForm(
      banner
        ? {
            isActive: banner.isActive,
            priority: banner.priority,
            startDate: banner.startDate,
            endDate: banner.endDate,
            target: banner.target,
            bannerAlertType: banner.bannerAlertType,
            content: banner.content,
            layout: banner.layout,
            titulo: banner.titulo,
            imagen: banner.imagen,
            cta: banner.cta,
          }
        : VACIO
    );
  }, [abierto, banner]);

  const set = <K extends keyof BannerFormulario>(campo: K, valor: BannerFormulario[K]) =>
    setForm((f) => ({ ...f, [campo]: valor }));

  const guardando = crear.isPending || actualizar.isPending;

  const guardar = async () => {
    setError(null);
    try {
      if (banner) {
        await actualizar.mutateAsync({ id: banner.bannerAlertId, cambios: form });
      } else {
        await crear.mutateAsync(form);
      }
      onCerrar();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  const ayudaLayout = LAYOUTS_BANNER.find((l) => l.value === form.layout)?.ayuda;

  return (
    <Modal
      isOpen={abierto}
      onClose={onCerrar}
      size="2xl"
      kicker="Banners"
      title={banner ? 'Editar banner' : 'Nuevo banner'}
      description="El contenido se publica sin desplegar el portal. Se sanea en el servidor antes de guardarse y otra vez al servirse."
      icon={<MegaphoneIcon className="h-6 w-6" />}
      data-testid="banner-form-modal"
      footer={
        <ModalFooter>
          <Button variant="secondary" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={guardar} isLoading={guardando} data-testid="banner-form-guardar">
            {banner ? 'Guardar cambios' : 'Crear banner'}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-5">
        {error && <Alert variant="danger">{error}</Alert>}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Select
              label="Composición"
              value={form.layout}
              onChange={(e) => set('layout', e.target.value as BannerLayout)}
              data-testid="banner-form-layout"
            >
              {LAYOUTS_BANNER.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </Select>
            {ayudaLayout && (
              <p className="mt-1.5 text-xs text-secondary-500 dark:text-secondary-400">{ayudaLayout}</p>
            )}
          </div>

          <Select
            label="Tipo"
            value={form.bannerAlertType}
            onChange={(e) => set('bannerAlertType', e.target.value)}
          >
            {TIPOS_BANNER.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
        </div>

        {form.layout === 'destacado' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Título"
              value={form.titulo ?? ''}
              onChange={(e) => set('titulo', e.target.value)}
              placeholder="Ya puedes emitir notas de crédito"
            />
            <Input
              label="Imagen (URL https)"
              value={form.imagen ?? ''}
              onChange={(e) => set('imagen', e.target.value)}
              placeholder="https://…"
            />
          </div>
        )}

        <div>
          <Textarea
            label={form.layout === 'html' ? 'HTML' : 'Contenido'}
            rows={form.layout === 'html' ? 8 : 4}
            value={form.content}
            onChange={(e) => set('content', e.target.value)}
            placeholder={
              form.layout === 'html'
                ? '<div style="padding:24px;background:#14243A;color:#fff">…</div>'
                : 'Admite <strong>, <a href="…"> y listas.'
            }
            data-testid="banner-form-contenido"
          />
          {form.layout === 'html' && (
            <p className="mt-1.5 text-xs text-warning-700 dark:text-warning-400">
              Solo funciona el atributo <code>style</code> en línea. Las clases de Tailwind y los
              bloques <code>&lt;style&gt;</code> no llegan al portal, así que aquí no hay responsive
              ni modo oscuro.
            </p>
          )}
        </div>

        {form.layout === 'destacado' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Texto del botón"
              value={form.cta?.texto ?? ''}
              onChange={(e) =>
                set('cta', e.target.value || form.cta?.destino
                  ? { texto: e.target.value, destino: form.cta?.destino ?? '' }
                  : undefined)
              }
              placeholder="Ver el manual"
            />
            <Input
              label="Destino del botón"
              value={form.cta?.destino ?? ''}
              onChange={(e) =>
                set('cta', e.target.value || form.cta?.texto
                  ? { texto: form.cta?.texto ?? '', destino: e.target.value }
                  : undefined)
              }
              placeholder="/invoices  o  https://…"
            />
          </div>
        )}

        {/* La previa va aquí y no al final: es la herramienta de redacción, y
            abajo del todo quedaba tapada por el pie del modal — había que
            desplazarse para ver lo que se estaba escribiendo. Fechas, roles y
            el interruptor no cambian cómo se ve, así que van después. */}
        <div>
          <p className="mb-2 text-[12px] font-bold uppercase tracking-[0.14em] text-secondary-500">
            Vista previa
          </p>
          {/* Usa el MISMO componente que el portal, así que lo que se ve aquí es
              lo que verá el cliente. El HTML no pasa por el saneador del
              servidor todavía, así que la previa puede mostrar de más: lo que
              se guarde vendrá recortado. */}
          <BannerContenido
            banner={{
              id: 'preview',
              type: form.bannerAlertType,
              layout: form.layout,
              contentHtml: form.content,
              priority: form.priority,
              titulo: form.titulo,
              imagen: form.imagen,
              cta: form.cta,
            }}
          />
        <div className="grid gap-4 sm:grid-cols-3">
          <Input
            type="date"
            label="Desde"
            value={isoAInputDate(form.startDate)}
            onChange={(e) => set('startDate', aIso(e.target.value))}
          />
          <Input
            type="date"
            label="Hasta"
            value={isoAInputDate(form.endDate)}
            onChange={(e) => set('endDate', aIso(e.target.value, true))}
          />
          <Input
            type="number"
            label="Prioridad"
            value={String(form.priority)}
            onChange={(e) => set('priority', Number(e.target.value) || 0)}
          />
        </div>

        <MultiSelect
          label="Roles que lo ven"
          options={ROLES_BANNER.map((r) => ({ id: r.value, label: r.label }))}
          selected={form.target.roles}
          onChange={(roles) => set('target', { ...form.target, roles })}
          placeholder="Todos los roles"
          data-testid="banner-form-roles"
        />
        <p className="-mt-3 text-xs text-secondary-500 dark:text-secondary-400">
          Sin seleccionar nada, lo ven todos los roles.
        </p>

        <MultiSelect
          label="Portales donde se muestra"
          options={PORTALES_BANNER.map((p) => ({ id: p.value, label: p.label }))}
          selected={form.target.portals}
          onChange={(portals) => set('target', { ...form.target, portals })}
          placeholder="Ambos portales"
          data-testid="banner-form-portales"
        />
        <p className="-mt-3 text-xs text-secondary-500 dark:text-secondary-400">
          Sin seleccionar nada, se muestra en ambos portales (legado y nuevo).
        </p>

        <div className="flex items-center gap-3 rounded-lg border border-secondary-200 px-4 py-3 dark:border-secondary-700">
          <Switch checked={form.isActive} onChange={(v) => set('isActive', v)} label="Activo" />
          <span className="text-sm text-secondary-500 dark:text-secondary-400">
            Apagarlo lo retira de todos los clientes de inmediato.
          </span>
        </div>

        </div>
      </div>
    </Modal>
  );
}
